import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiArrowUpLight } from 'react-icons/pi';
import GlassCard from '../brand/GlassCard';
import Button from '../TailwindComponents/Button';
import Dropdown from '../TailwindComponents/Dropdown';
import MentionInput from './MentionInput';
import { DEFAULT_POST_TYPES, FeedUser } from './feedModel';
import { feedEventLabel } from './feedPresentation';

export interface FeedComposerProps {
  /** Resolves once the post has been accepted (or rejected) by the caller. */
  onPost: (post: { event_type: string; message: string }) => Promise<void> | void;
  /** Mention targets. */
  members?: FeedUser[];
  /** The types this surface allows. Defaults to the standard five. */
  postTypes?: string[];
  /** 'pane' collapses to a single line - a docked pane has no room for a type row. */
  density?: 'canvas' | 'pane';
  className?: string;
  'data-tour-id'?: string;
}

const insertMention = (text: string, memberName: string): string => {
  const lastAt = text.lastIndexOf('@');
  if (lastAt === -1) return `${text}@[${memberName}] `;
  const before = text.substring(0, lastAt);
  const after = text.substring(lastAt).split(/\s/).slice(1).join(' ');
  return `${before}@[${memberName}] ${after ? after + ' ' : ''}`;
};

/**
 * The post box, pinned to the bottom of the feed (`flex-shrink-0`) so the list
 * above it scrolls under a fixed composer rather than the whole column moving.
 */
const FeedComposer: React.FC<FeedComposerProps> = ({
  onPost,
  members = [],
  postTypes = DEFAULT_POST_TYPES,
  density = 'canvas',
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [postType, setPostType] = useState(postTypes[0] || 'update');
  const [posting, setPosting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);

  const compact = density === 'pane';

  const typeOptions = useMemo(
    () => postTypes.map((type) => ({ value: type, label: feedEventLabel(type) })),
    [postTypes]
  );

  const suggestions = members.filter((m) =>
    `${m.first_name} ${m.last_name}`.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const submit = async () => {
    if (!message.trim() || posting) return;
    setPosting(true);
    try {
      await onPost({ event_type: postType, message });
      setMessage('');
      setPostType(postTypes[0] || 'update');
    } catch {
      // The feed has already rolled the post back and the page has already
      // shown the error. Keep the text so the retry is one click away.
    } finally {
      setPosting(false);
    }
  };

  return (
    <GlassCard padding="sm" className={clsx('flex-shrink-0', className)} {...rest}>
      <MentionInput
        value={message}
        onChange={setMessage}
        onMentionQuery={(query) => {
          setMentionQuery(query);
          setShowMentions(query.length > 0);
          setMentionIndex(0);
        }}
        onSubmit={submit}
        placeholder={
          compact
            ? t('Write an update…')
            : t("What's new? Type @ to mention team members")
        }
        rows={compact ? 1 : 3}
        showSuggestions={showMentions}
        suggestions={suggestions}
        onSuggestionSelect={(_id, memberName) => {
          setMessage((current) => insertMention(current, memberName));
          setShowMentions(false);
          setMentionQuery('');
        }}
        selectedSuggestionIndex={mentionIndex}
        onSelectedIndexChange={setMentionIndex}
      />

      <div className={clsx('mt-2 flex items-center gap-2', compact && 'justify-end')}>
        {!compact && (
          /* Same trigger as every other Dropdown (h-9, rounded-md) on a
             white fill, so it reads as a control rather than a grey chip. */
          <div className="surface-canvas">
            <Dropdown
              options={typeOptions}
              value={postType}
              onChange={(value) => setPostType(Array.isArray(value) ? value[0] : value)}
              width="w-40"
            />
          </div>
        )}
        <div className="ms-auto flex gap-2">
          {!compact && (
            <Button
              variant="outline"
              size="small"
              onClick={() => setMessage('')}
              disabled={!message.trim() || posting}
            >
              {t('Cancel')}
            </Button>
          )}
          <Button
            size="small"
            onClick={submit}
            disabled={!message.trim() || posting}
            isLoading={posting}
            icon={<PiArrowUpLight className="h-4 w-4" />}
          >
            {t('Post')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
};

export default FeedComposer;
